// InvoiceSelectionContext.js - Updated for backend filtering
import React, { createContext, useContext, useState, useMemo, useCallback, useRef } from 'react';

const InvoiceSelectionContext = createContext();

export const InvoiceSelectionProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  
  // Card click state - tracks which card was clicked (status-based cards)
  const [selectedCard, setSelectedCard] = useState(null);
  
  // Filters state - updated to match backend expectations
  const [filters, setFilters] = useState({
    status: "",
    clientName: "", 
    minAmount: "",
    maxAmount: "",
    fromDate: "",
    toDate: ""
  });
  
  // Pagination state
  const [pagination, setPagination] = useState({
    page: 0,
    limit: 5,
    hasMore: true,
    totalInvoices: 0
  });
  
  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  
  // Store filtered invoices
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  
  const lastUpdateTimeRef = useRef(0);
  const [refreshKey, setRefreshKey] = useState(0);

  const triggerInvoiceRefresh = useCallback(() => {
    setRefreshKey(prevKey => prevKey + 1);
  }, []);
  
  const setFilteredInvoicesCallback = useCallback((invoices) => {
    const now = Date.now();
    
    // Throttling to prevent excessive updates
    if (now - lastUpdateTimeRef.current < 50) {
      console.log('Throttling setFilteredInvoices update');
      return;
    }
    lastUpdateTimeRef.current = now;
    
    console.log('Invoice Context setFilteredInvoices called with:', invoices?.length || 0, 'invoices');
    
    // Always update with new data
    setFilteredInvoices(Array.isArray(invoices) ? [...invoices] : []);
  }, []);
  
  const buildQueryParams = useCallback(() => {
    const params = new URLSearchParams();
    
    // Pagination
    params.set('page', pagination.page.toString());
    params.set('limit', pagination.limit.toString());
    
    // Search term
    if (searchTerm && searchTerm.trim()) {
      params.set('search', searchTerm.trim());
    }
    
    // Card-based filtering (onClick parameter for invoices)
    if (selectedCard) {
      params.set('onClick', selectedCard);
    }
    
    // FIXED: Apply all filters with proper empty string checking
    if (filters.status && filters.status.trim() !== "") {
      params.set('status', filters.status.trim());
    }
    if (filters.clientName && filters.clientName.trim() !== "") {
      params.set('clientName', filters.clientName.trim());
    }
    if (filters.minAmount && filters.minAmount.trim() !== "") {
      params.set('minAmount', filters.minAmount.trim());
    }
    if (filters.maxAmount && filters.maxAmount.trim() !== "") {
      params.set('maxAmount', filters.maxAmount.trim());
    }
    if (filters.fromDate && filters.fromDate.trim() !== "") {
      params.set('fromDate', filters.fromDate.trim());
    }
    if (filters.toDate && filters.toDate.trim() !== "") {
      params.set('toDate', filters.toDate.trim());
    }
    
    console.log('Invoice API params:', params.toString());
    return params.toString();
  }, [pagination, searchTerm, selectedCard, filters]);
  
  // Reset pagination when filters change
  const resetPagination = useCallback(() => {
    setPagination(prev => ({
      ...prev,
      page: 0,
      hasMore: true
    }));
  }, []);
  
  // Clear all filters and reset state
  // const clearAllFilters = useCallback(() => {
  //   console.log('Clearing all invoice filters and resetting state');
    
  //   setFilters({
  //     status: "",
  //     clientName: "",
  //     minAmount: "",
  //     maxAmount: "",
  //     fromDate: "",
  //     toDate: ""
  //   });
    
  //   setSearchTerm('');
  //   setSelectedCard(null);
  //   resetPagination();
    
  //   // Clear the filtered invoices
  //   setFilteredInvoices([]);
    
  //   // IMPORTANT: Trigger refresh to fetch default data
  //   setRefreshKey(prevKey => prevKey + 1);
  // }, [resetPagination]);
  
  const clearAllFilters = useCallback(() => {
  console.log('Clearing all invoice filters and resetting state');
  
  setFilters({
    status: "",
    clientName: "",
    minAmount: "",
    maxAmount: "",
    fromDate: "",
    toDate: ""
  });
  
  setSearchTerm('');
  setSelectedCard(null);
  resetPagination();
  
  // Clear the filtered invoices
  setFilteredInvoices([]);
  
  // IMPORTANT: Trigger refresh to fetch default data
  setRefreshKey(prevKey => prevKey + 1);
}, [resetPagination, setFilters, setSearchTerm]);

  // Handle card click
  // const handleCardClick = useCallback((cardType) => {
  //   setSelectedCard(prevCard => prevCard === cardType ? null : cardType);
  //   resetPagination();
  // }, [resetPagination]);

  const handleCardClick = useCallback((cardType) => {
  // Special handling for "total" card - always clear filters and show all data
  if (cardType === "total" || cardType === null) {
    setSelectedCard(null);
    // Also clear all filters when clicking total
    setFilters({
      status: "",
      clientName: "", 
      minAmount: "",
      maxAmount: "",
      fromDate: "",
      toDate: ""
    });
    setSearchTerm('');
    resetPagination();
    // Trigger refresh to fetch all data
    setRefreshKey(prevKey => prevKey + 1);
  } else {
    // For other cards, toggle selection as before
    setSelectedCard(prevCard => prevCard === cardType ? null : cardType);
    resetPagination();
  }
}, [resetPagination, setFilters, setSearchTerm]);

  
  // Stable setters
  const setFiltersStable = useCallback((newFilters) => {
    setFilters(prevFilters => {
      const hasChanged = Object.keys(newFilters).some(key => 
        newFilters[key] !== prevFilters[key]
      );
      
      if (hasChanged) {
        resetPagination();
        return newFilters;
      }
      
      return prevFilters;
    });
  }, [resetPagination]);
  
  const setSearchTermStable = useCallback((term) => {
    if (term !== searchTerm) {
      setSearchTerm(term);
      resetPagination();
    }
  }, [searchTerm, resetPagination]);
  
  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return selectedCard !== null ||
           searchTerm.trim() !== '' ||
           Object.values(filters).some(value => value && value.trim() !== '');
  }, [selectedCard, searchTerm, filters]);
  
  // Update pagination info
  const updatePaginationInfo = useCallback((totalInvoices, hasMore) => {
    setPagination(prev => ({
      ...prev,
      totalInvoices,
      hasMore
    }));
  }, []);
  
  // Load more invoices (for pagination)
  const loadMore = useCallback(() => {
    if (pagination.hasMore) {
      setPagination(prev => ({
        ...prev,
        page: prev.page + 1
      }));
    }
  }, [pagination.hasMore]);
  
  const contextValue = useMemo(() => ({
    // Card selection
    selectedCard,
    setSelectedCard,
    handleCardClick,
    
    // Filters
    filters,
    setFilters: setFiltersStable,
    
    // Search
    searchTerm,
    setSearchTerm: setSearchTermStable,
    
    // Pagination
    pagination,
    setPagination,
    resetPagination,
    updatePaginationInfo,
    loadMore,
    
    // Filtered invoices
    filteredInvoices,
    setFilteredInvoices: setFilteredInvoicesCallback,
    refreshKey,
    triggerInvoiceRefresh,
    
    // Utilities
    buildQueryParams,
    clearAllFilters,
    hasActiveFilters,
    setIsLoading,
    isLoading,
  }), [
    selectedCard,
    handleCardClick,
    filters,
    setFiltersStable,
    searchTerm,
    setSearchTermStable,
    pagination,
    resetPagination,
    updatePaginationInfo,
    loadMore,
    filteredInvoices,
    setFilteredInvoicesCallback,
    buildQueryParams,
    clearAllFilters,
    hasActiveFilters,
    refreshKey,
    triggerInvoiceRefresh,
    setIsLoading,
    isLoading,
  ]);
  
  return (
    <InvoiceSelectionContext.Provider value={contextValue}>
      {children}
    </InvoiceSelectionContext.Provider>
  );
};

export const useInvoiceSelection = () => {
  const context = useContext(InvoiceSelectionContext);
  if (!context) {
    throw new Error('useInvoiceSelection must be used within an InvoiceSelectionProvider');
  }
  return context;
};